// ============================================================
//  ALTIMO Medical Center — Tarifario.jsx
//  Ubicación: src/components/Tarifario.jsx
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';

const TIPO_COLOR = {
  Procedimiento: { bg: '#e2d9f3', color: '#4a2080' },
  Examen:        { bg: '#fff3cd', color: '#856404' },
  Honorario:     { bg: '#f4c0d1', color: '#72243E' },
  Insumo:        { bg: '#d1ecf1', color: '#0c5460' },
  Paquete:       { bg: '#d4edda', color: '#155724' },
};

const S = {
  pagina: {
    background: '#f0f4f8', minHeight: '100vh',
    padding: '20px', fontFamily: 'Segoe UI, sans-serif',
  },
  card: {
    background: '#fff', borderRadius: '10px',
    border: '1px solid #dde3ea', padding: '18px', marginBottom: '16px',
  },
  cardTitulo: {
    fontSize: '12px', fontWeight: '700', color: '#1B2A5E',
    textTransform: 'uppercase', letterSpacing: '.05em',
    marginBottom: '14px', paddingBottom: '10px', borderBottom: '2px solid #e8f0fb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left', padding: '8px 12px', fontSize: '11px',
    color: '#6c757d', fontWeight: '600', borderBottom: '2px solid #e8f0fb', background: '#f8fafd',
  },
  td: { padding: '9px 12px', borderBottom: '1px solid #eef1f6', color: '#333' },
  pill: (tipo) => ({
    display: 'inline-block', padding: '2px 9px', borderRadius: '99px',
    fontSize: '10px', fontWeight: '700',
    background: TIPO_COLOR[tipo]?.bg || '#e2e3e5',
    color: TIPO_COLOR[tipo]?.color || '#333',
  }),
  input: {
    width: '100%', fontSize: '13px', padding: '8px 10px',
    border: '1px solid #c0ccda', borderRadius: '7px',
    background: '#fff', color: '#333', outline: 'none', boxSizing: 'border-box',
  },
  label: {
    display: 'block', fontSize: '11px', fontWeight: '600', color: '#6c757d',
    marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.04em',
  },
  campo: { marginBottom: '12px' },
  btnPrimario: {
    background: '#1D9E75', color: '#fff', border: 'none',
    borderRadius: '7px', padding: '8px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
  },
  btnAzul: {
    background: '#1B2A5E', color: '#fff', border: 'none',
    borderRadius: '7px', padding: '7px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  },
  btnSecundario: {
    background: '#fff', color: '#495057', border: '1px solid #c0ccda',
    borderRadius: '7px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer',
  },
  alerta: (tipo) => ({
    padding: '10px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', marginBottom: '12px',
    background: tipo === 'ok' ? '#d4edda' : '#f8d7da',
    color:      tipo === 'ok' ? '#155724' : '#721c24',
    border:     `1px solid ${tipo === 'ok' ? '#c3e6cb' : '#f5c6cb'}`,
  }),
  modal: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.45)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modalCard: {
    background: '#fff', borderRadius: '12px', padding: '28px',
    width: '420px', border: '1px solid #dde3ea',
  },
};

const TIPOS = ['Procedimiento', 'Examen', 'Honorario', 'Insumo', 'Paquete'];
const FORM_VACIO = { tipo: 'Examen', codigo: '', nombre: '', precio: '' };

export default function Tarifario({ usuario }) {
  const [items,    setItems]    = useState([]);
  const [filtro,   setFiltro]   = useState('Todos');
  const [busqueda, setBusqueda] = useState('');
  const [modal,    setModal]    = useState(null); // null | 'nuevo' | item (para editar)
  const [form,     setForm]     = useState(FORM_VACIO);
  const [mensaje,  setMensaje]  = useState(null);
  const [guardando,setGuardando]= useState(false);

  const cargar = useCallback(async () => {
    const r = await api.tarifario.listar();
    if (r.ok) setItems(r.data);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const abrirNuevo = () => { setForm(FORM_VACIO); setModal('nuevo'); setMensaje(null); };

  const abrirEditar = (item) => {
    setForm({ tipo: item.tipo, codigo: item.codigo || '', nombre: item.nombre, precio: item.precio });
    setModal(item);
    setMensaje(null);
  };

  const guardar = async () => {
    if (!form.nombre || !form.precio) {
      setMensaje({ tipo: 'err', texto: 'Nombre y precio son obligatorios.' }); return;
    }
    setGuardando(true);
    let r;
    if (modal === 'nuevo') {
      r = await api.tarifario.crear({
        tipo: form.tipo, codigo: form.codigo || null,
        nombre: form.nombre, precio: parseFloat(form.precio),
      });
    } else {
      r = await api.tarifario.editar({
        id: modal.id, nombre: form.nombre,
        precio: parseFloat(form.precio), activo: 1,
      });
    }
    setGuardando(false);
    if (r.ok) {
      setModal(null);
      cargar();
      setMensaje({ tipo: 'ok', texto: modal === 'nuevo' ? 'Ítem creado.' : 'Ítem actualizado.' });
      setTimeout(() => setMensaje(null), 3000);
    } else {
      setMensaje({ tipo: 'err', texto: r.error });
    }
  };

  const filtrados = items.filter(i => {
    const porTipo = filtro === 'Todos' || i.tipo === filtro;
    const porBus  = !busqueda || i.nombre.toLowerCase().includes(busqueda.toLowerCase())
                              || (i.codigo || '').toLowerCase().includes(busqueda.toLowerCase());
    return porTipo && porBus;
  });

  return (
    <div style={{ fontFamily:'Segoe UI, sans-serif' }}>
      {/* ══ Header módulo — estandarizado ══ */}
      <div style={{ background:'linear-gradient(135deg,#1B2A5E,#0f1c40)', padding:'16px 24px' }}>
        <div style={{ fontSize:'19px', fontWeight:'800', color:'#fff', marginBottom:'3px' }}>
          💲 Módulo de Tarifario
        </div>
        <div style={{ fontSize:'12px', color:'rgba(255,255,255,.8)' }}>
          Precios centralizados de servicios y procedimientos
        </div>
      </div>

      <div style={S.pagina}>

      {mensaje && <div style={S.alerta(mensaje.tipo)}>{mensaje.texto}</div>}

      <div style={S.card}>
        <div style={S.cardTitulo}>
          <span>Tarifario centralizado ({filtrados.length} ítems)</span>
          <button style={S.btnPrimario} onClick={abrirNuevo}>+ Nuevo ítem</button>
        </div>

        {/* Filtros y búsqueda */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input style={{ ...S.input, maxWidth: '260px' }}
            placeholder="Buscar por nombre o código..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          {['Todos', ...TIPOS].map(t => (
            <button key={t}
              style={{
                padding: '6px 14px', borderRadius: '7px', fontSize: '12px', fontWeight: '600',
                cursor: 'pointer', border: 'none',
                background: filtro === t ? '#1B2A5E' : '#e8f0fb',
                color: filtro === t ? '#fff' : '#1B2A5E',
              }}
              onClick={() => setFiltro(t)}>{t}
            </button>
          ))}
        </div>

        <table style={S.tabla}>
          <thead>
            <tr>
              {['Tipo', 'Código', 'Nombre', 'Precio', ''].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtrados.map(item => (
              <tr key={item.id}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafd'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={S.td}><span style={S.pill(item.tipo)}>{item.tipo}</span></td>
                <td style={{ ...S.td, color: '#6c757d', fontSize: '12px' }}>{item.codigo || '—'}</td>
                <td style={S.td}><strong>{item.nombre}</strong></td>
                <td style={{ ...S.td, fontWeight: '700', color: '#1B2A5E' }}>
                  ${Number(item.precio).toFixed(2)}
                </td>
                <td style={S.td}>
                  <button style={S.btnAzul} onClick={() => abrirEditar(item)}>Editar</button>
                </td>
              </tr>
            ))}
            {filtrados.length === 0 && (
              <tr><td colSpan="5" style={{ ...S.td, textAlign: 'center', color: '#6c757d', padding: '30px' }}>
                No se encontraron ítems.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Modal nuevo / editar ── */}
      {modal !== null && (
        <div style={S.modal} onClick={() => setModal(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '15px', fontWeight: '800', color: '#1B2A5E', marginBottom: '20px' }}>
              {modal === 'nuevo' ? 'Nuevo ítem del tarifario' : `Editar: ${modal.nombre}`}
            </div>

            {mensaje && <div style={S.alerta(mensaje.tipo)}>{mensaje.texto}</div>}

            <div style={S.campo}>
              <label style={S.label}>Tipo *</label>
              <select style={S.input} value={form.tipo} onChange={e => set('tipo', e.target.value)}
                disabled={modal !== 'nuevo'}>
                {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {modal === 'nuevo' && (
              <div style={S.campo}>
                <label style={S.label}>Código (opcional)</label>
                <input style={S.input} value={form.codigo}
                  onChange={e => set('codigo', e.target.value)} placeholder="Ej: EX-005" />
              </div>
            )}
            <div style={S.campo}>
              <label style={S.label}>Nombre *</label>
              <input style={S.input} value={form.nombre}
                onChange={e => set('nombre', e.target.value)} placeholder="Nombre del servicio" />
            </div>
            <div style={S.campo}>
              <label style={S.label}>Precio ($) *</label>
              <input style={S.input} type="number" min="0" step="0.01"
                value={form.precio} onChange={e => set('precio', e.target.value)} />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
              <button style={S.btnPrimario} onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
              <button style={S.btnSecundario} onClick={() => setModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
